import { useEffect, useState } from 'react'

import { Button } from 'common/components/Button/Button'
import { Input } from 'common/components/Input/Input'
import { Select } from 'common/components/Select/Select'

import type { MedicalIssueType } from 'features/Schedule/api/medicalIssues.types'

import styles from './medicalissues.module.scss'

const ISSUE_TYPE_OPTIONS = [
  { value: 'medical_problem', label: 'Medical' },
  { value: 'cosmetic', label: 'Cosmetic' },
]

const SELECT_PATIENT_FIRST_ERROR = 'Select a patient first'

type InlineCreateIssueProps = {
  hasPatient: boolean
  disabled?: boolean
  isSubmitting: boolean
  onAdd: (title: string, type: MedicalIssueType) => Promise<boolean>
}

export const InlineCreateIssue = ({
  hasPatient,
  disabled = false,
  isSubmitting,
  onAdd,
}: InlineCreateIssueProps) => {
  const [title, setTitle] = useState<string>('')
  const [issueType, setIssueType] = useState<MedicalIssueType>('medical_problem')
  const [noPatientError, setNoPatientError] = useState<string | null>(null)

  useEffect(() => {
    if (hasPatient) setNoPatientError(null)
  }, [hasPatient])

  const isAddBlocked = disabled || isSubmitting || !title.trim()

  const handleAdd = async () => {
    if (isAddBlocked) return
    if (!hasPatient) {
      setNoPatientError(SELECT_PATIENT_FIRST_ERROR)
      return
    }
    const isAdded = await onAdd(title.trim(), issueType)
    if (isAdded) {
      setTitle('')
      setIssueType('medical_problem')
    }
  }

  return (
    <div className={styles.inlineRow}>
      <div className={styles.inlineTitle}>
        <Input
          name='new-medical-issue-title'
          placeholder='New problem'
          maxLength={255}
          value={title}
          disabled={disabled || isSubmitting}
          onChange={(event) => {
            setTitle(event.target.value)
            setNoPatientError(null)
          }}
          onPressEnter={(event) => {
            event.preventDefault()
            handleAdd()
          }}
          error={noPatientError ?? undefined}
        />
      </div>
      <div className={styles.inlineType}>
        <Select
          listOptions={ISSUE_TYPE_OPTIONS}
          value={issueType}
          disabled={disabled || isSubmitting}
          onChange={(value: MedicalIssueType) => setIssueType(value)}
        />
      </div>
      <Button disabled={isAddBlocked} loading={isSubmitting} onClick={handleAdd}>
        Add
      </Button>
    </div>
  )
}
